import { useCallback, useMemo } from 'react';
import { ApolloError } from '@apollo/client';

import {
  useProvisionPandaDocStudentsMutation,
  ProvisionResultItem,
  ProvisionDocOverrideInput,
} from 'core/graphql';

export type ProvisionResultRow = ProvisionResultItem;

export interface IProvisionStudentsArgs {
  groupId: string;
  contributionAmount: number;
  currency: string;
  documents: ProvisionDocOverrideInput[];
}

interface IProvisionStudentsState {
  /** Per-document outcomes; `null` until the mutation has run. */
  results: ProvisionResultRow[] | null;
  /** Top-level error (`errors` JSON string), e.g. the group was deleted. */
  topLevelErrors: string | null;
  loading: boolean;
  error?: ApolloError;
}

type ProvisionStudentsHook = [
  (args: IProvisionStudentsArgs) => void,
  IProvisionStudentsState,
];

/** Wraps the `provisionStudents` mutation for the folder screen. */
export const useProvisionStudents = (): ProvisionStudentsHook => {
  const [
    mutate,
    { data, loading, error },
  ] = useProvisionPandaDocStudentsMutation();

  const provision = useCallback(
    ({
      groupId,
      contributionAmount,
      currency,
      documents,
    }: IProvisionStudentsArgs) => {
      if (loading) {
        return;
      }
      mutate({
        variables: { groupId, contributionAmount, currency, documents },
      });
    },
    [mutate, loading],
  );

  const payload = data?.provisionStudents;

  // Memoised so the array keeps a stable reference between renders — consumers
  // dedupe the post-mutation effect by comparing `results` identity.
  const results = useMemo(
    () =>
      payload?.results
        ? payload.results.filter((item): item is ProvisionResultRow =>
            Boolean(item),
          )
        : null,
    [payload],
  );

  return [
    provision,
    {
      results,
      topLevelErrors: payload?.errors ?? null,
      loading,
      error,
    },
  ];
};
